import React from 'react'
import { Box, Chip, Typography } from '@material-ui/core'
import { ContractLifecycleStatus } from '../../models/Company/types'
import { formatDate } from './utils'

const STATUS_LABELS: Record<ContractLifecycleStatus, string> = {
    active: 'Active',
    ending_soon: 'Ending Soon',
    auto_renew_notice: 'Auto-Renew Notice',
    expired: 'Expired',
    no_dates: 'No Dates',
}

const STATUS_STYLES: Record<
    ContractLifecycleStatus,
    { backgroundColor: string; color: string }
> = {
    active: { backgroundColor: '#dcfce7', color: '#166534' },
    ending_soon: { backgroundColor: '#fef3c7', color: '#92400e' },
    auto_renew_notice: { backgroundColor: '#ffedd5', color: '#9a3412' },
    expired: { backgroundColor: '#fee2e2', color: '#991b1b' },
    no_dates: { backgroundColor: '#f3f4f6', color: '#4b5563' },
}

export const lifecycleRowShadeStyles = {
    rowExpired: {
        backgroundColor: '#fdf2f2',
        '& td': {
            backgroundColor: '#fdf2f2',
        },
        '&:hover td': {
            backgroundColor: '#f8e4e4',
        },
    },
    rowEndingSoon: {
        backgroundColor: '#fff8ea',
        '& td': {
            backgroundColor: '#fff8ea',
        },
        '&:hover td': {
            backgroundColor: '#fff0d0',
        },
    },
    rowAutoRenew: {
        backgroundColor: '#fff5ee',
        '& td': {
            backgroundColor: '#fff5ee',
        },
        '&:hover td': {
            backgroundColor: '#ffe9dc',
        },
    },
    rowNoDates: {
        backgroundColor: '#f4f5f7',
        '& td': {
            backgroundColor: '#f4f5f7',
        },
        '&:hover td': {
            backgroundColor: '#eaedf1',
        },
    },
    rowActive: {
        backgroundColor: '#f2f9f3',
        '& td': {
            backgroundColor: '#f2f9f3',
        },
        '&:hover td': {
            backgroundColor: '#e4f2e6',
        },
    },
}

export const lifecycleRowClassName = (
    classes: {
        row: string
        rowExpired: string
        rowEndingSoon: string
        rowAutoRenew: string
        rowNoDates: string
        rowActive: string
    },
    status?: ContractLifecycleStatus | null,
) => {
    if (status === 'expired') return `${classes.row} ${classes.rowExpired}`
    if (status === 'ending_soon')
        return `${classes.row} ${classes.rowEndingSoon}`
    if (status === 'auto_renew_notice')
        return `${classes.row} ${classes.rowAutoRenew}`
    if (status === 'no_dates') return `${classes.row} ${classes.rowNoDates}`
    if (status === 'active') return `${classes.row} ${classes.rowActive}`
    return classes.row
}

interface ContractStatusChipProps {
    status?: ContractLifecycleStatus | null
    autoRenew?: boolean
    endDate?: string | null
}

export const autoRenewDateCaption = (
    autoRenew?: boolean,
    endDate?: string | null,
    status?: ContractLifecycleStatus | null,
) => {
    if (!autoRenew) return null
    const formatted = endDate ? formatDate(endDate) : ''
    if (formatted && formatted !== '—') {
        return status === 'expired'
            ? `Was set to renew ${formatted}`
            : `Renews ${formatted}`
    }
    if (status === 'expired') return 'Auto-renew was on'
    return null
}

export const ContractStatusChip: React.FC<ContractStatusChipProps> = ({
    status,
    autoRenew,
    endDate,
}) => {
    if (!status) return null
    const caption = autoRenewDateCaption(autoRenew, endDate, status)
    return (
        <Box>
            <Chip
                label={STATUS_LABELS[status] || status}
                size="small"
                style={STATUS_STYLES[status]}
            />
            {caption && (
                <Typography
                    variant="caption"
                    color={status === 'expired' ? 'error' : 'textSecondary'}
                    style={{ display: 'block', marginTop: 2 }}
                >
                    {caption}
                </Typography>
            )}
        </Box>
    )
}
